<template>
  <div>
    <v-overlay :value="loading" class="align-center justify-center">
      <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-snackbar v-model="error.show">
      <p class="py-0 my-0 font-weight-black">{{ error.title }}</p>
      {{ error.message }}

      <template v-slot:action="{ attrs }">
        <v-btn color="blue" text v-bind="attrs" @click="error.show = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="detalhes.show" max-width="700">
      <v-card v-if="detalhes.cliente">
        <v-card-title class="pb-2">
          <v-icon left color="blue">mdi-information-outline</v-icon>
          Composição do custo
          <v-spacer></v-spacer>
          <v-btn icon @click="detalhes.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="pb-0">
          <span class="font-weight-medium">{{ detalhes.cliente.nome }}</span>
          <v-chip x-small class="ml-2" :color="getCor(detalhes.cliente.plano)" dark>
            {{ getPlanoNome(detalhes.cliente.plano) }}
          </v-chip>
        </v-card-subtitle>
        <v-card-text class="pt-4">
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Item</th>
                  <th class="text-center">Qtd.</th>
                  <th class="text-right">Valor unitário</th>
                  <th class="text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in detalhes.cliente.custosItens" :key="index">
                  <td>
                    <div class="d-flex align-center">
                      <v-icon small color="grey" class="mr-2">{{ item.icone }}</v-icon>
                      <div>
                        <div class="font-weight-medium">{{ item.nome }}</div>
                        <div class="text-caption grey--text">{{ item.descricao }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="text-center">{{ item.qtd }}</td>
                  <td class="text-right">{{ formatarMoeda(item.unitario) }}</td>
                  <td class="text-right font-weight-medium">{{ formatarMoeda(item.subtotal) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-divider class="my-3"></v-divider>

          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-subtitle-2 grey--text">Estimativa de custo total:</span>
            <span class="text-subtitle-1 font-weight-bold red--text">
              {{ formatarMoeda(detalhes.cliente.custos) }}
            </span>
          </div>
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-subtitle-2 grey--text">Estimativa de faturamento:</span>
            <span class="text-subtitle-1 font-weight-bold green--text">
              {{ formatarMoeda(detalhes.cliente.valor) }}
            </span>
          </div>
          <div class="d-flex justify-space-between align-center">
            <span class="text-subtitle-2 grey--text">Estimativa de lucro:</span>
            <span class="text-h6 font-weight-bold" :class="detalhes.cliente.lucro >= 0 ? 'orange--text' : 'red--text'">
              {{ formatarMoeda(detalhes.cliente.lucro) }}
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-toolbar flat style="background-color: #f3f3f3">
      <h3 class="grey--text">
        <v-icon left color="grey">mdi-chart-arc</v-icon>
        Estimativas para {{ proximoMes }}
      </h3>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="grey" dark class="mb-2 ml-2" v-bind="attrs" v-on="on" @click="carregarDados()">
            <v-icon small> mdi-refresh </v-icon>
          </v-btn>
        </template>
        <span>Atualizar dados</span>
      </v-tooltip>
    </v-toolbar>

    <v-container fluid class="pa-4">
      <!-- Cards de Resumo -->
      <v-row>
        <v-col cols="12" md="3">
          <v-card elevation="2" class="pa-4">
            <div class="d-flex align-center">
              <v-avatar color="blue" size="56" class="mr-4">
                <v-icon dark size="28">mdi-account-group</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text">Total de clientes</div>
                <div class="text-h4 font-weight-bold blue--text">{{ resumo.totalClientes }}</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card elevation="2" class="pa-4">
            <div class="d-flex align-center">
              <v-avatar color="green" size="56" class="mr-4">
                <v-icon dark size="28">mdi-currency-brl</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text">Estimativa de faturamento</div>
                <div class="text-h4 font-weight-bold green--text">{{ formatarMoeda(resumo.estimativaTotal) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card elevation="2" class="pa-4">
            <div class="d-flex align-center">
              <v-avatar color="red" size="56" class="mr-4">
                <v-icon dark size="28">mdi-currency-brl</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text">Estimativa de custos</div>
                <div class="text-h4 font-weight-bold red--text">{{ formatarMoeda(resumo.estimativaCustosTotal) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card elevation="2" class="pa-4">
            <div class="d-flex align-center">
              <v-avatar color="orange" size="56" class="mr-4">
                <v-icon dark size="28">mdi-calendar-month</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text">Estimativa de lucro</div>
                <div class="text-h4 font-weight-bold orange--text">{{ formatarMoeda(resumo.estimativaLucroTotal) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Cards por Plano -->
      <v-row class="mt-4">
        <v-col cols="12">
          <h3 class="grey--text">
            <v-icon left color="grey">mdi-chart-pie</v-icon>
            Distribuição por planos
          </h3>
        </v-col>
        <v-col cols="12" sm="6" md="3" v-for="(plano, index) in planosResumo" :key="index">
          <v-card elevation="2" :class="`plano-card plano-${plano.value}`">
            <v-card-title class="pb-1">
              <span class="text-subtitle-1 font-weight-medium">{{ plano.nome }}</span>
              <v-spacer></v-spacer>
              <v-chip small :color="getCor(plano.value)" dark>
                {{ plano.qtd }} cliente(s)
              </v-chip>
            </v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-caption grey--text">Valor do plano:</span>
                <span class="font-weight-medium">{{ formatarMoeda(plano.preco) }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="d-flex justify-space-between align-center">
                <span class="text-subtitle-2 grey--text">Total:</span>
                <span class="text-h6 font-weight-bold" :class="`${getCor(plano.value)}--text`">
                  {{ formatarMoeda(plano.total) }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabela Detalhada -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card elevation="2">
            <v-card-title>
              <v-icon left color="grey">mdi-format-list-bulleted</v-icon>
              Detalhamento por cliente
              <v-spacer></v-spacer>
              <v-text-field v-model="pesquisar" append-icon="mdi-magnify" label="Pesquisar" single-line hide-details dense
                style="max-width: 300px;"></v-text-field>
            </v-card-title>
            <v-data-table :headers="headers" :items="clientesComPlano" :search="pesquisar" :loading="loadingLista"
              loading-text="Carregando..." :footer-props="{
                'items-per-page-options': [10, 25, 50, 100, 250, 500, 1000, -1],
              }" :items-per-page="10" @dblclick:row="($event, { item }) => acessarCliente(item)" locale="pt" dense>
              <template v-slot:item.nome="{ item }">
                <span class="font-weight-medium">{{ item.nome }}</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small color="blue" class="ml-1" v-bind="attrs" v-on="on"
                      @click.stop="abrirDetalhes(item)">
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Ver composição do custo</span>
                </v-tooltip>
              </template>
              <template v-slot:item.plano="{ item }">
                <v-chip small :color="getCor(item.plano)" dark>
                  {{ getPlanoNome(item.plano) }}
                </v-chip>
              </template>
              <template v-slot:item.vencimento="{ item }">
                <span class="font-weight-medium">{{ new Date(item.vencimento).toLocaleDateString('pt-BR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) }}</span>
              </template>
              <template v-slot:item.valor="{ item }">
                <span class="font-weight-medium">{{ formatarMoeda(item.valor) }}</span>
              </template>
              <template v-slot:item.custos="{ item }">
                <span class="font-weight-medium">{{ formatarMoeda(item.custos) }}</span>
              </template>
              <template v-slot:item.lucro="{ item }">
                <span class="font-weight-medium">{{ formatarMoeda(item.lucro) }}</span>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip small :color="item.atrasado ? 'orange' : 'green'" dark>
                  {{ item.atrasado ? 'Atrasado' : 'Em dia' }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { fetchApiClientes, responsavelGeralDados, loginSistema } from "../../services/api";
import { verificarSessao, getMes } from "../../utils/utils";
import { carregarValores } from "../../services/valores";

export default {
  name: "Faturamento",
  data() {
    return {
      loading: false,
      loadingLista: true,
      error: {
        show: false,
        message: "",
        title: "",
      },
      pesquisar: "",
      detalhes: {
        show: false,
        cliente: null,
      },
      token: localStorage.getItem("token"),
      clientes: [],
      precosPlanos: {
        0: 0,
        1: 0,
        2: 0,
        3: 0,
      },
      mesAtual: getMes(),
      headers: [
        { text: "Cliente", value: "nome" },
        { text: "Plano", value: "plano" },
        { text: "Próximo vencimento", value: "vencimento" },
        { text: "Estimativa de faturamento", value: "valor" },
        { text: "Estimativa de custo", value: "custos" },
        { text: "Estimativa de lucro", value: "lucro" },
        { text: "Status", value: "status" },
      ],
    };
  },
  computed: {
    clientesComPlano() {
      return this.clientes
        .filter((c) => c.ativo && !/demo/gi.test(c.nome) && c.block !== "🔴")
        .map((c) => {
          const plano = c.analytics?.plano ?? 0;
          const detalhes = this.detalharCustosCliente(c);
          const custos = detalhes.total;
          const valor = this.precosPlanos[plano] || 0;
          return {
            nome: c.nome,
            plano: plano,
            vencimento: c.vencimento,
            valor: valor,
            custos: custos,
            custosItens: detalhes.itens,
            lucro: (valor - custos),
            atrasado: c.situacao != "🟢",
          };
        });
    },
    planosResumo() {
      const resumoPorPlano = {};

      this.planos.forEach((p) => {
        resumoPorPlano[p.value] = {
          value: p.value,
          nome: p.text,
          qtd: 0,
          preco: parseFloat(this.precosPlanos[p.value]) || 0,
          total: 0,
          subtotal: 0,
          subtotalCustos: 0,
        };
      });

      this.clientesComPlano.forEach((c) => {
        if (resumoPorPlano[c.plano]) {
          resumoPorPlano[c.plano].qtd++;
          resumoPorPlano[c.plano].total += c.valor;
          resumoPorPlano[c.plano].subtotal += (c.valor - c.custos);
          resumoPorPlano[c.plano].subtotalCustos += c.custos;
        }
      });

      return Object.values(resumoPorPlano);
    },
    resumo() {
      return {
        totalClientes: this.clientesComPlano.length,
        estimativaTotal: this.planosResumo.reduce((acc, p) => acc + p.total, 0),
        estimativaLucroTotal: this.planosResumo.reduce((acc, p) => acc + p.subtotal, 0),
        estimativaCustosTotal: this.clientesComPlano.reduce((acc, c) => acc + c.custos, 0),
      };
    },
    proximoMes() {
      const hoje = new Date();
      const proximo = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
      return proximo.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    },
  },
  async created() {
    window.addEventListener("offline", () => (this.loading = true));
    window.addEventListener("online", () => (this.loading = false));
    /**
     *
     */
    verificarSessao(this.carregarDados);
    setTimeout(verificarSessao, 1 * 60000);
  },
  methods: {
    setError(error) {
      this.error = {
        show: true,
        title: "Ops, falhou!",
        message: error ? error : "Falha nao identificada, tente novamente.",
      };
    },
    formatarMoeda(valor) {
      return (valor || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    getCor(plano) {
      plano = parseFloat(plano);
      const cores = {
        0: "grey",
        1: "red",
        2: "purple",
        3: "black",
      };
      return cores[plano] || "grey";
    },
    getPlanoNome(plano) {
      plano = parseFloat(plano);
      const p = this.planos.find((item) => item.value === plano);
      return p ? p.text : "Personalizado";
    },
    calcularCustosCliente(cliente) {
      return this.detalharCustosCliente(cliente).total;
    },
    detalharCustosCliente(cliente) {
      const itens = [
        {
          icone: "mdi-store",
          nome: "Licença base (revenda)",
          descricao: "Custo fixo por loja ativa",
          qtd: 1,
          unitario: this.valorRevenda,
          subtotal: this.valorRevenda,
        },
      ];

      if (!cliente.analytics) {
        return { itens, total: this.somarItens(itens) };
      }

      const nfces = parseInt(cliente.analytics?.fiscal30dias ?? 0);
      const nfes = parseInt(cliente.analytics?.nfes30dias ?? 0);
      const dfes = parseInt(cliente.analytics?.dfes30dias ?? 0);
      const nfses = parseInt(cliente.analytics?.nfses30dias ?? 0);
      if (nfces > 0 || nfes > 0 || dfes > 0 || nfses > 0) {
        itens.push({
          icone: "mdi-file-document-outline",
          nome: "Módulo fiscal",
          descricao: `Emissões nos últimos 30 dias: ${nfces} NFC-e, ${nfes} NF-e, ${dfes} DF-e, ${nfses} NFS-e`,
          qtd: 1,
          unitario: this.valorFiscal,
          subtotal: this.valorFiscal,
        });
      }

      const modulosMes = cliente.analytics[`modulos_mes${this.mesAtual}`];
      if (modulosMes) {
        const modulos = [
          {
            chave: "gamazap_campanhas",
            icone: "mdi-whatsapp",
            nome: "GamaZap campanhas",
            valor: this.valorGamaZapCampanhas,
          },
          { chave: "robo", icone: "mdi-robot", nome: "Robô", valor: this.valorRobo },
          { chave: "gtef", icone: "mdi-credit-card-outline", nome: "G-TEF", valor: this.valorGtef },
          { chave: "gtef_sitef", icone: "mdi-credit-card-outline", nome: "G-TEF SiTef", valor: this.valorGtefSitef },
          { chave: "gtef_scope", icone: "mdi-credit-card-outline", nome: "G-TEF Scope", valor: this.valorGtefScope },
          { chave: "gpos", icone: "mdi-point-of-sale", nome: "G-POS", valor: this.valorGpos },
          { chave: "gmesa", icone: "mdi-table-chair", nome: "G-Mesas", valor: this.valorGmesas },
          { chave: "gtotem", icone: "mdi-tablet", nome: "G-Totem", valor: this.valorGtotem },
          {
            chave: "suporte_dedicado",
            icone: "mdi-headset",
            nome: "Suporte dedicado",
            valor: this.valorSuporte,
          },
        ];

        modulos.forEach((modulo) => {
          const qtd = parseInt(modulosMes[modulo.chave] ?? 0);
          if (qtd > 0) {
            itens.push({
              icone: modulo.icone,
              nome: modulo.nome,
              descricao: `${qtd} uso(s) registrado(s) no mês vingente`,
              qtd: qtd,
              unitario: modulo.valor,
              subtotal: qtd * modulo.valor,
            });
          }
        });
      }

      return { itens, total: this.somarItens(itens) };
    },
    somarItens(itens) {
      return itens.reduce((acc, item) => acc + item.subtotal, 0);
    },
    abrirDetalhes(item) {
      this.detalhes = {
        show: true,
        cliente: item,
      };
    },
    async carregarDados() {
      this.loadingLista = true;
      try {
        const [clientesRes, responsavelRes] = await Promise.all([
          fetchApiClientes(this.token),
          responsavelGeralDados(this.token),
          carregarValores(),
        ]);

        this.clientes = clientesRes?.data?.clientes || [];

        const responsavel = responsavelRes?.data?.responsavel || {};
        this.precosPlanos = {
          0: parseFloat(responsavel.preco_plano_0) || 0,
          1: parseFloat(responsavel.preco_plano_1) || 0,
          2: parseFloat(responsavel.preco_plano_2) || 0,
          3: parseFloat(responsavel.preco_plano_3) || 0,
        };
      } catch (error) {
        this.setError(error?.response?.data?.message || error?.message || "Erro ao carregar dados");
      } finally {
        this.loadingLista = false;
      }
    },
    acessarCliente(cliente) {
      window.open(loginSistema(cliente.nome, this.baseUrl, this.token));
    },
  },
};
</script>

<style scoped>
.plano-card {
  border-left: 4px solid;
  transition: transform 0.2s ease-in-out;
}

.plano-card:hover {
  transform: translateY(-2px);
}

.plano-0 {
  border-left-color: #9e9e9e;
}

.plano-1 {
  border-left-color: red;
}

.plano-2 {
  border-left-color: #9c27b0;
}

.plano-3 {
  border-left-color: black;
}
</style>
